import { Archive, ArchiveRestore, PlusCircle, X } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { Pressable } from "react-native";
import { View, XStack, YGroup, YStack } from "tamagui";
import { ModuleType } from "../../../templates/types";
import { ActivityUI } from "../../activities/ActivityUI";
import { useApi } from "../../core/apis";
import { useStores } from "../../core/stores";
import { usePlanNavigation } from "../../plans/usePlanNavigation";
import { AdSenseInline } from "../../ui/AdSenseInline";
import { BlockLoading, ModuleBlockSkeleton } from "../../ui/Block";
import { Body, BodySkeleton } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { Button } from "../../ui/Button";
import { CenterContent } from "../../ui/CenterContent";
import { EmojiPicker } from "../../ui/EmojiPicker";
import { Input } from "../../ui/InputNew";
import { Label } from "../../ui/Label";
import { ModuleIcon } from "../../ui/ModuleIcon";
import { PopoverHelp } from "../../ui/PopoverHelp";
import { Separator } from "../../ui/Separator";
import { Sheet } from "../../ui/Sheet";
import { SubtextSkeleton } from "../../ui/Subtext";
import { Title, TitleSkeleton } from "../../ui/Title";
import type BaseModuleModel from "./BaseModuleModel";
import { ModuleOnboardingBlock } from "./ModuleOnboardingBlock";

// biome-ignore lint/suspicious/noExplicitAny: Because
type AnyModule = BaseModuleModel<any, any>;

export const ModuleSetupTitle: React.FC<{
  module: AnyModule;
  placeholder?: string;
}> = observer(({ module, placeholder }) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  return (
    <>
      <EmojiPicker
        sheetId="emoji"
        onSelect={(emoji) => {
          module.setEmoji(emoji.emoji, true);
          setShowEmojiPicker(false);
        }}
        open={showEmojiPicker}
        onOpenChange={(open: boolean) => setShowEmojiPicker(open)}
      />
      <XStack>
        <View
          onPress={() => setShowEmojiPicker(true)}
          cursor="pointer"
          borderWidth={1}
          borderRightWidth={0}
          borderColor={"$divider"}
          backgroundColor={"$background"}
          alignItems="center"
          padding="$space.medium"
        >
          <ModuleIcon module={module} />
        </View>
        <Input
          defaultValue={module.data.title ?? ""}
          type="border"
          placeholder={placeholder ?? "Set title..."}
          blurOnSubmit
          submitOnEnter
          onSubmitEditing={(e) => {
            module.setTitle(e.nativeEvent.text, true);
          }}
        />
      </XStack>
    </>
  );
});

export const ModuleSetupBase: React.FC<{
  module: AnyModule;
  title?: string;
  description?: string;
  titlePlaceholder?: string;
}> = observer(({ module, title, description, titlePlaceholder }) => {
  return (
    <YStack gap="$larger">
      <YGroup gap="$medium">
        <Title>{title ?? "Setup card"}</Title>
        <Body>
          {description ??
            "Let's start with setting a title and icon for the card"}
        </Body>
      </YGroup>
      <ModuleSetupTitle module={module} placeholder={titlePlaceholder} />
    </YStack>
  );
});

const ModuleSetupSheet: React.FC<{ module: AnyModule }> = observer(
  ({ module }) => {
    const [open, setIsOpen] = React.useState(true);
    return (
      <Sheet sheetId="module_setup" open={open}>
        <YStack gap="$larger">
          {module.renderBlockSetup ? (
            module.renderBlockSetup()
          ) : (
            <YStack gap="$larger">
              <XStack
                justifyContent="space-between"
                alignItems="center"
                width={"100%"}
              >
                <Title>Setup</Title>
                <Pressable
                  onPress={() => {
                    setIsOpen(false);
                  }}
                >
                  <X color="$muted" />
                </Pressable>
              </XStack>
              <Body>
                Let's start with setting a title and icon for the card
              </Body>
              <ModuleSetupTitle module={module} />
            </YStack>
          )}
          <Button onPress={module.init.bind(module)}>Get started!</Button>
        </YStack>
      </Sheet>
    );
  },
);

export const ModuleUIRenderer = observer(
  ({
    uid,
    dataUid,
    showAddStep,
  }: {
    uid: string;
    dataUid?: string;
    showAddStep?: boolean;
  }) => {
    const { modulesStore } = useStores();
    const { modulesApi } = useApi();

    const module = modulesStore.byId.get(uid);

    React.useEffect(() => {
      const unsub = modulesApi.watchItem(uid);
      return () => {
        unsub();
      };
    }, [modulesApi, uid]);

    if (!module) {
      return <BlockLoading />;
    }

    const showComments =
      module.plan?.showComments && module.data.type !== ModuleType.PlanInfo;

    const isStatic = module.isStatic;

    // @ts-expect-error
    const isPlaywrightTest = !!window.__IS_PLAYWRIGHT_TEST__;
    const showAds = !isPlaywrightTest && !module.plan.data.businessPlanUid;

    return (
      <>
        <YStack marginBottom="$medium" gap="$medium">
          {module.data.archived && (
            <>
              <Box>
                <CenterContent
                  icon={Archive}
                  title={"Archived!"}
                  subTitle={
                    "This is currently archived. Please reset if you need to work on it again."
                  }
                />
              </Box>
              <Button
                onPress={() => {
                  module.plan.unArchiveModule(module.uid);
                }}
                style={{ alignSelf: "stretch" }}
                icon={ArchiveRestore}
              >
                Restore this
              </Button>
            </>
          )}

          {isStatic && module.renderStatic()}
          {!isStatic && module.render()}

          {showAddStep && (
            <>
              <Separator />
              <View
                onPress={() => module.plan.setShowAddStepSheet(true)}
                cursor="pointer"
                gap={"$small"}
                style={{ opacity: 0.75 }}
                alignItems="center"
              >
                <XStack gap="$small" alignItems="center">
                  <PlusCircle color={"$muted"} />
                  <Label color={"$muted"}>Add step</Label>
                  <PopoverHelp description="Steps help you plan and make decisions, create a step and set a helpful title and icon to get going" />
                </XStack>
              </View>
            </>
          )}

          {showAds && <AdSenseInline />}

          {showComments ? (
            <ActivityUI moduleUid={module.uid} dataUid={dataUid} />
          ) : undefined}
        </YStack>

        {!module.data.initiated ? (
          <ModuleSetupSheet module={module} />
        ) : undefined}
      </>
    );
  },
);

export const ModuleBlockRenderer = observer(
  ({
    uid,
    noPadding,
    active,
    onPress,
  }: {
    uid: string;
    noPadding?: boolean;
    active?: boolean;
    upcoming?: boolean;
    onPress?: () => void;
  }) => {
    const { modulesStore } = useStores();
    const { modulesApi } = useApi();
    const navigation = usePlanNavigation();

    const module = modulesStore.byId.get(uid);
    const isStatic = module?.isStatic;

    React.useEffect(() => {
      const unsub = modulesApi.watchItem(uid);
      return () => {
        unsub();
      };
    }, [modulesApi, uid]);

    if (!module) {
      // Should be plan info, is there a better way of knowing?
      if (noPadding) {
        return (
          <Box>
            <TitleSkeleton />
            <BodySkeleton />
            <SubtextSkeleton />
          </Box>
        );
      }

      return <ModuleBlockSkeleton />;
    }

    if (module.isOnboarding) {
      return <ModuleOnboardingBlock module={module} />;
    }

    return (
      <View
        opacity={module.isDone || active ? 1 : 0.25}
        cursor="pointer"
        style={{ flexGrow: 1, width: "100%" }}
        onPress={() => {
          onPress
            ? onPress()
            : navigation.navigate("Module", {
                planUid: module.data.planUid,
                moduleUid: module.uid,
              });
        }}
      >
        {isStatic ? module.renderStaticBlock() : module.renderBlock({})}
        {module.hasNewActivity && (
          <View
            position="absolute"
            right={0}
            top={0}
            bottom={0}
            borderColor={"$primary"}
            borderLeftWidth={4}
            borderRadius={10}
          />
        )}
      </View>
    );
  },
);
